import { useEffect, useState } from "react";
import { Phone, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Overline } from "@/components/Reveal";
import { SITE, waMenuLink } from "@/data/site";

// Compatibilità: le vecchie voci erano stringhe ("Nome" oppure "Nome :: prezzo"),
// le nuove sono oggetti {name, price, available} letti dal database.
const normalize = (item) => {
  if (typeof item === "string") {
    const [name, price] = item.split("::").map((s) => s.trim());
    return { name, price, available: true };
  }
  return item;
};

const Col = ({ title, items }) => (
  <div>
    <h3 className="text-xs uppercase tracking-[0.25em] text-terracotta-light font-medium">{title}</h3>
    <ul className="mt-5 space-y-3">
      {items.length > 0 ? (
        items.map((raw) => {
          const d = normalize(raw);
          return (
            <li key={d.name} className="flex items-baseline justify-between gap-4 border-b border-cream/10 pb-3">
              <span className={`font-serif text-xl md:text-2xl leading-snug ${d.available ? "text-cream" : "text-cream/40 italic"}`}>
                {d.name}
              </span>
              <span className="flex items-baseline gap-3 shrink-0">
                {!d.available && (
                  <span className="text-[11px] uppercase tracking-[0.2em] text-terracotta-light font-sans font-semibold">Esaurito</span>
                )}
                {d.price && (
                  <span className="font-sans text-sm font-semibold text-terracotta-light whitespace-nowrap">{d.price}</span>
                )}
              </span>
            </li>
          );
        })
      ) : (
        <li className="font-serif italic text-lg text-cream/40">In preparazione…</li>
      )}
    </ul>
  </div>
);

export default function PiattiDelGiorno() {
  const [menu, setMenu] = useState(null);

  useEffect(() => {
    api.get("/daily-menu").then((r) => setMenu(r.data)).catch(() => {});
  }, []);

  const dateLabel = menu?.date
    ? new Date(`${menu.date}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })
    : "";

  return (
    <section id="piatti-del-giorno" className="py-24 md:py-32 lg:py-40 bg-charred text-cream relative overflow-hidden">
      <span aria-hidden="true" className="absolute top-10 -left-6 font-serif italic text-[18vw] leading-none text-cream/[0.04] pointer-events-none select-none">oggi</span>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <Overline light>Piatti del giorno</Overline>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-none tracking-tight">
                Cosa abbiamo<br /><span className="italic text-terracotta-light">preparato oggi?</span>
              </h2>
              {dateLabel && (
                <p className="mt-5 text-sm uppercase tracking-[0.2em] text-cream/50" data-testid="daily-menu-date">
                  Menù di {dateLabel}
                </p>
              )}
              <p className="mt-6 text-cream/60 text-sm md:text-base leading-relaxed">
                {menu?.note || "Ogni giorno un menù diverso, con primi, secondi, contorni e specialità appena preparate."}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row lg:flex-col gap-3">
                <a href={SITE.phoneHref} data-testid="daily-call-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-cream px-6 py-3 text-sm font-semibold hover:bg-terracotta-dark transition-colors">
                  <Phone size={15} /> Prenota telefonicamente
                </a>
                <a href={waMenuLink} target="_blank" rel="noopener noreferrer" data-testid="daily-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-cream/30 text-cream px-6 py-3 text-sm font-semibold hover:bg-cream hover:text-charred transition-colors">
                  <MessageCircle size={15} /> Scrivici su WhatsApp
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal delay={0.15} className="grid grid-cols-1 sm:grid-cols-3 gap-10" >
              <Col title="Primi" items={menu?.primi || []} />
              <Col title="Secondi" items={menu?.secondi || []} />
              <Col title="Contorni" items={menu?.contorni || []} />
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-10 text-xs text-cream/40 leading-relaxed">
                Disponibilità fino a esaurimento. Alcuni piatti possono terminare prima dell'orario di chiusura: per assicurarti i tuoi preferiti, consigliamo la prenotazione.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
